// The live tenant-key session: what this tab is holding, who to tell when
// that changes, and when to let go of it.
//
// Split out of `vault-unlock.ts` so that file can be the public API and
// this one can be the state machine. The dependency is strictly one way —
// `vault-unlock.ts` imports this; nothing here imports it back — which is
// what lets `adoptKeyHandle` be the single funnel every unlock path
// (a pasted recovery file, a key loaded from IndexedDB) goes through.
//
// Note what is NOT a path into that funnel: a message from another tab.
// The cross-tab channel is lock-only — see its module docblock for the
// argument — so the session becomes non-null in exactly one way, and
// that way starts in THIS tab, from either a human paste or this
// browser's own stored record.
//
// What is held is a NON-EXTRACTABLE `CryptoKey`, not bytes. Script on this
// page can use it (ECDH, and therefore `open()`); it cannot read it. ADR
// 0009 has the argument for why that is the whole point: an XSS can use
// the key while the page is open, so the blast radius ends with the tab
// rather than being permanent and portable.
//
// SSR safe: nothing touches `window`, `indexedDB` or `BroadcastChannel` at
// module load. The cross-tab channel comes up on the first client-side
// `subscribeUnlocked`, which is what `useIsUnlocked` calls on mount.
import { bufferSource } from '@stoaria/vault-core'
import type { ImportedRecipientKey } from '@stoaria/vault-core'
import {
  noteActivity as slideIdleDeadline,
  startIdleCountdown,
  stopIdleCountdown,
} from './vault-idle-lock.ts'
import { broadcastLock, connectUnlockChannel } from './vault-unlock-channel.ts'

const X25519 = { name: 'X25519' } as const

/** Everything this tab knows about the key it is holding. One per tab,
 * shared by every subscriber — the same single-instance shape the old
 * module-level `Uint8Array` had. */
export interface UnlockedSession {
  /** The handle plus its public half, in exactly the form `open()` takes.
   * The public half has to be carried: a non-extractable key cannot
   * produce it, and it is an HKDF salt input on every decrypt. */
  recipient: ImportedRecipientKey
  /** Base64url public key as the server reports it. Half of the storage
   * record's key, and what a re-pasted recovery file is checked against
   * by the raw-key ceremony. */
  tenantPubkey: string
  rootId: string | null
}

let session: UnlockedSession | null = null

// Subscribers notified whenever the session flips between null and
// non-null, so components can react to unlock/lock without polling.
const listeners = new Set<() => void>()

function notifyListeners(): void {
  for (const listener of listeners) listener()
}

export function getSession(): UnlockedSession | null {
  return session
}

export function isUnlocked(): boolean {
  return session !== null
}

/** Subscribes to unlock/lock transitions; returns an unsubscribe function.
 * Built for `useSyncExternalStore` (see `useIsUnlocked`), but usable
 * standalone. Also the hook point where this tab joins the cross-tab
 * channel: it is the first thing a client calls and never runs on the
 * server. */
export function subscribeUnlocked(listener: () => void): () => void {
  connectChannel()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Idempotent; safe to call from anywhere that might be the first thing
 * to run on the client.
 *
 * One handler, and that is the security property rather than a
 * simplification: the only thing a sibling tab — or anything else that
 * can post on a same-origin channel — can make this module do is drop
 * the key. There is no message that installs one. See the channel
 * module's docblock for why honouring a forged lock is safe and why
 * honouring anything else would not be. */
export function connectChannel(): void {
  connectUnlockChannel({
    onRemoteLock: () => {
      // No re-broadcast: the tab that pressed Lock already told everyone,
      // and echoing would have every tab answering every other one.
      clearSession()
    },
  })
}

/** Takes a session, arms the idle countdown, and tells this tab's
 * subscribers. The one path by which `session` becomes non-null. */
function adopt(next: UnlockedSession): void {
  session = next
  startIdleCountdown(lock)
  notifyListeners()
}

function clearSession(): void {
  session = null
  stopIdleCountdown()
  notifyListeners()
}

/** Drops the key in this tab and in every other tab of this browser.
 *
 * Idempotent, and it notifies even when nothing was held: the operator
 * who cannot remember whether this tab still has the key is exactly the
 * person who needs to press this and be certain.
 *
 * It does NOT touch the stored record. Lock is "stop holding this now";
 * "forget this device" is a different, louder decision and lives in
 * `forgetStoredKey`. Conflating them would mean the idle timeout quietly
 * uninstalled the thing that makes reloads free. */
export function lock(): void {
  clearSession()
  broadcastLock()
}

/**
 * Makes an already-imported, non-extractable X25519 handle this tab's
 * session.
 *
 * This is the funnel. The store hands back a `CryptoKey` straight from
 * IndexedDB and the paste path imports one from bytes, but both arrive
 * here, so there is exactly one place that decides what an unlocked
 * session looks like and exactly one place that arms the clock.
 *
 * `publicKey` has to be passed rather than derived: deriving it needs an
 * extractable import (see `ImportedRecipientKey` in `@stoaria/vault-core`),
 * which is the very thing this design refuses to do.
 */
export function adoptKeyHandle(
  key: CryptoKey,
  tenantPubkey: string,
  publicKey: Uint8Array,
  rootId: string | null,
): void {
  connectChannel()
  adopt({ recipient: { key, publicKey }, tenantPubkey, rootId })
}

/**
 * Imports PKCS8 bytes as a key WebCrypto will never give back, and makes
 * the result this tab's session.
 *
 * `extractable: false` is the entire security claim of ADR 0009, so the
 * import is written here once and nowhere else — every path that starts
 * from bytes (a pasted recovery file, a freshly rotated key) comes
 * through this function and therefore cannot accidentally ask for an
 * extractable key.
 *
 * Returns the handle as well as adopting it, because the caller usually
 * has to hand the very same object to the store: re-importing the bytes
 * a second time would work, but it would mean the key this tab uses and
 * the key on disk are two objects that merely ought to agree.
 *
 * SECURITY: does NOT zero `pkcs8`. The caller owns that buffer and knows
 * whether it is a copy; `replacePrivateKey`'s caller, for one, still has
 * to render those exact bytes as the new recovery file.
 */
export async function adoptImportedKey(
  pkcs8: Uint8Array,
  tenantPubkey: string,
  publicKey: Uint8Array,
  rootId: string | null,
): Promise<CryptoKey> {
  const key = await globalThis.crypto.subtle.importKey(
    'pkcs8',
    bufferSource(pkcs8),
    X25519,
    false,
    ['deriveBits'],
  )
  adoptKeyHandle(key, tenantPubkey, publicKey, rootId)
  return key
}

/** Slides the idle deadline back out to a full 8 hours in THIS tab.
 *
 * Three callers, and that is the complete list: `withPrivateKey`,
 * `withRawPrivateKey`, and the "stay unlocked" button on the idle
 * warning. Nothing is wired to pointer or key events, in this module or
 * anywhere else — so what the idle timeout measures is the key going
 * unused, not the mouse going still.
 *
 * That is the stricter reading and the right one. An operator who spends
 * the afternoon on the Sessions page has not touched the tenant key, and
 * a timer a stray mousemove over a background tab can reset is not a
 * timer. The cost is that the warning can appear mid-session to someone
 * who is present, which is exactly what the "stay unlocked" button is
 * for.
 *
 * It is also local-only. Activity used to be broadcast so a busy tab
 * kept its siblings alive, which meant any script on the origin could
 * keep every tab unlocked indefinitely by posting a ping a minute.
 * Liveness is something a tab observes about itself; it is not something
 * it can be told. Sliding is in any case bounded — see
 * `MAX_SESSION_MS`. */
export function noteActivity(): void {
  slideIdleDeadline()
}

/** Runs `fn` with the tenant key HANDLE, or throws if locked — the one
 * place every unlock-gated operation in the app reads the session from.
 *
 * `fn` gets an `ImportedRecipientKey`, not bytes: pass it straight to
 * `open()`. Bytes come only from `withRawPrivateKey`, which is a
 * recovery-file ceremony and not this. */
export async function withPrivateKey<T>(
  fn: (recipient: ImportedRecipientKey) => Promise<T> | T,
): Promise<T> {
  if (session === null)
    throw new Error('tenant key is locked — paste the recovery file first')
  noteActivity()
  return fn(session.recipient)
}
